import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { Button, Field, Groupper, Header, Input, Message, Image, Icon, Dropdown, Modal, Toast } from 'react-frontier';
import { Countdown } from '@arema/components';
import { OrderData } from '../CheckoutClasses';
import { addCommas, formatCreditCard, formatExpiration, formatSeatNumber, unix, bindFormChange, uniqueArray, bindClose, bindClick, randomRange, toCents } from '@arema/components/Util';
import { Location, PaymentMethods, PaymentMethodType, PaymentPlan, SetLoading } from '@arema/components/Classes';
import { useTitle, useValidator } from '@arema/components/Hooks';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CDN_URL, OPENPAY_KEY, OPENPAY_MERCHANT, PAGE_PUBLIC, PLACEHOLDER_IMG } from '../CheckoutConfig';
import { usePayfront, ApplePayButton } from 'react-payfront';
import useOpenpay from '../useOpenpay';
import Accordion from '../components/Accordion';
import API from '../CheckoutAPI';
import CheckoutForm from './CheckoutForm';
import CheckoutEnd from './CheckoutEnd';
import Validator from '@arema/components/Validator';
import moment from 'moment';

import '../styles/checkout_payment.scss';
import '../styles/checkout_end.scss';

interface PaymentScreenProps{
	payment?: boolean,
}

enum WalletType{
	APPLE_PAY = 1,
	GOOGLE_WALLET = 2,
}

enum Modals{
	COUPON = 1
}

var Payment = (props: PaymentScreenProps)=>{
	var params = useParams();
	var Payfront = usePayfront();
	var OpenPay = useOpenpay();
	var { t } = useTranslation();
	var { setTitle } = useTitle();
	var [searchParams, setSearchParams] = useSearchParams();
	var [data, setData] = useState<OrderData>(null);
	var [sending, setSending] = useState<boolean>(false);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [errorPrompts, setErrorPrompts] = useState<string[]>(null);
	var [cardPrompts, setCardPrompts] = useState<string[]>(null);
	var [locationPrompts, setLocationPrompts] = useState<string[]>(null);
	var [checkoutPrompts, setCheckoutPrompts] = useState<string>(null);
	var [expired, setExpired] = useState<boolean>(false);
	var [isWalletPayment, setIsWalletPayment] = useState<WalletType>(null);
	var [paymentForce, setPaymentForce] = useState<boolean>(props.payment);
	var [paymentPlans, setPaymentPlans] = useState<PaymentPlan[]>(null);
	var [paymentError, setPaymentError] = useState<string>(null);
	var CodeData = useValidator({
		code: '',
		applying: false
	}, {
		code: [{
			rule: /^[A-Za-z0-9][A-Za-z0-9]{2,31}$/gi, prompt: t('payment.coupon.invalid_code')
		}]
	})
	var [location, setLocation] = useState<Location>({
		street: '',
		city: '',
		state: '',
		zipcode: '',
		country: 'México',
		neighborhood: '',
		exterior_number: '',
		interior_number: '',
	});
	var [cardForm, setCardForm] = useState({
		card_name: '',
		card_number: '',
		card_expiration: '',
		card_code: '',
		postal_code: '',
		payment_plan: -1,
	});
	var [formData, setFormData] = useState<{
		first_name: string,
		last_name: string,
		email: string,
		payment_method: number,
		coupon: string,
		zipcode: string,
		phone: string,
	}>({
		first_name: '',
		last_name: '',
		email: '',
		payment_method: null,
		coupon: '',
		zipcode: '',
		phone: '',
	});

	useEffect(()=>{
		if(!data && params && params.token){
			API.getOrder(params.token).then(res=>{
				if(res.error){
					if(res.code=='C-EXPORD') return setExpired(true);
					else return setLoadError(res.message);
				}
				setData(res.data);
				setFormData({
					first_name: res.data.order.first_name,
					last_name: res.data.order.last_name,
					email: res.data.order.email,
					payment_method: null,
					coupon: '',
					zipcode: '',
					phone: '',
				});
				if(paymentForce && res.data && res.data.tickets){
					if(!res.data.tickets.find(a=>!a.paid)){
						setPaymentForce(false);
					}
				}
			}).catch(err=>{
				setLoadError(t('common.unexpected_code', { code: 'CHK-PYGTO-1' }));
			});
		}
		setTitle('Pago');
	}, [params]);

	useEffect(()=>{
		if(!data || !data.order) return;
		if(!paymentForce && (data.order.finished || data.order.paid)){
			setTitle(`${t('payment.title_purchase')} ${data.order.order_hash}`);
		}else{
			setTitle(`${t('payment.title_purchase')} ${data.order.order_hash}`);
		}
	}, [paymentForce, data]);

	useEffect(()=>{
		if(paymentPlans) return;
		if(formData?.payment_method){
			var method = data.payment_methods.find(a=>a.payment_method===formData?.payment_method);
			if(!method) return;
			if(method.payment_plans && !paymentPlans){
				getPaymentPlans();
			}
		}
	}, [formData?.payment_method, paymentPlans]);

	useEffect(()=>{
		if(!!searchParams.get('pe')){
			setPaymentError(t('common.card_rejected'))
			setSearchParams({}, { replace: true });
		}
	}, [searchParams]);

	var hasApplePayMethod = useMemo(()=>{
		if(!Payfront.hasApplePay || !data || !data.payment_methods) return false;
		return data && data.payment_methods && !!data.payment_methods.find(a=>a.payment_method===PaymentMethods.PAYFRONT);
	}, [Payfront.hasApplePay, data, data?.payment_methods]);

	var { tickets_total, commission_total, original_tickets_total, original_commission_total, has_discount, has_discount_commission } = useMemo(()=>{
		var tickets_total = 0, commission_total = 0;
		var original_tickets_total = 0, original_commission_total = 0;
		var has_discount = false, has_discount_commission = false;
		if(!data) return { tickets_total, commission_total, original_tickets_total, original_commission_total, has_discount, has_discount_commission };
		
		for(var i of data.tickets){
			if(i.paid) continue;
			tickets_total += i.ticket_cost;
			commission_total += i.commission;
			original_tickets_total += i.original_cost;
			original_commission_total += i.original_commission;
		}

		has_discount = toCents(tickets_total)!=toCents(original_tickets_total);
		has_discount_commission = toCents(commission_total)!=toCents(original_commission_total);

		return {
			tickets_total,
			commission_total,
			original_tickets_total,
			original_commission_total,
			has_discount,
			has_discount_commission
		};
	}, [data?.tickets]);

	var payment_cost = useMemo(()=>{
		if(!data || !data.order || !data.tickets || !data.payment_methods) return 0;
		var payment_cost = 0;
		var order_total = data.order.delivery_cost;
		for(var i of data.tickets){
			if(i.paid) continue;
			order_total += i.ticket_cost + i.commission;
		}

		if(formData.payment_method){
			var pm = data.payment_methods.find(a=>a.payment_method==formData.payment_method);
			if(pm){
				if(pm.type_id===PaymentMethodType.CARD){ // IS CARD
					payment_cost = pm.commission_amount + (data.order.card_commission || pm.commission_percent)*order_total;
				}else{
					payment_cost = pm.commission_amount+(order_total*pm.commission_percent);
				}
			}
		}
		return payment_cost;
	}, [data?.order, data?.tickets, formData?.payment_method, data?.payment_methods]);

	var total_final = useMemo(()=>{
		if(!data || !data.tickets) return 0;
		var total_final = 0;
		for(var i of data.tickets){
			if(i.paid) continue;
			total_final += i.ticket_cost+i.commission;
		}
		total_final += Math.max(data.order?.delivery_cost || 0, 0);
		total_final += payment_cost;
		return total_final;
	}, [payment_cost, data?.tickets, data?.order?.delivery_cost])

	var date_expiration = useMemo(()=>{
		if(!data || !data?.tickets) return null;
		var exp = null;
		for(var i of data.tickets){
			if(i.paid) continue;
			if(i.date_expiration){
				if(exp===null) exp = i.date_expiration;
				else exp = Math.min(i.date_expiration);
			}
		}

		return exp;
	}, [data, data?.tickets]);

	const OrderIdentifier = !!data?.order && (
		<div style={{ textAlign: 'center', color: 'gray', fontWeight: 'bold', marginTop: 5 }}>
			{t('common.order')} {data.order.order_id ? `@${data.order.order_id}` : data.order.order_hash}
		</div>
	);
	
	if(expired){
		return <div>
			<Header text={t('payment.expired.title')} subtext={t('payment.expired.text')} iconName='face-frown-open' />
			{!!paymentError && (
				<Message type={'error'} style={{ margin: '0 auto 10px auto', marginBottom: 15, maxWidth: 400 }}>
					{paymentError}
				</Message>
			)}
			<Button text={t('common.return')} style={{ display: 'block', margin: 'auto', width: 200 }} color='black' as={Link} to={PAGE_PUBLIC} />
			{OrderIdentifier}
		</div>
	}
	
	if(!data || loadError){
		return <Header text={loadError ? t('common.error') : t('payment.loading')} subtext={loadError || null} iconName={loadError ? 'face-frown-open' : null} loading={!loadError} />
	}

	if(!paymentForce && data && data.order && (data.order.finished || data.order.paid)){
		return <CheckoutEnd data={data} onForcePayment={()=>setPaymentForce(true)} />
	}

	var formFinish = ()=>{
		var o = {...data};
		o.order.form_answered = true;
		setData(o);
	}

	var getPaymentPlans = ()=>{
		if(!data.tickets || data.tickets.length==0) return;
		var events = uniqueArray(data.tickets.map(a=>a.event_id));
		if(!events || events.length==0) return;
		events = events.sort((a,b)=>a-b);
		API.getPaymentPlans(events).then(res=>{
			if(res.error) return;
			setPaymentPlans(res.data);
		}).catch(err=>{
			return;
		});
	}

	var submitPayment = async (setLoading: SetLoading)=>{
		setCheckoutPrompts(null);
		var { valid, prompts } = Validator(formData, {
			first_name: [
				{ rule: 'empty', prompt: t('payment.form.prompts.first_name.empty') },
				{ rule: 'minLength', skipEmpty: true, params: [3], prompt: t('payment.form.prompts.first_name.minLength') },
				{ rule: 'maxLength', params: [32], prompt: t('payment.prompts.first_name.maxLength') },
			],
			last_name: [
				{ rule: 'empty', prompt: t('payment.form.prompts.last_name.empty') },
				{ rule: 'minLength', skipEmpty: true, params: [3], prompt: t('payment.form.prompts.last_name.minLength') },
				{ rule: 'maxLength', params: [32], prompt: t('payment.form.prompts.last_name.maxLength') },
			],
			email: [{
				rule: 'email', prompt: t('payment.form.prompts.email')
			}],
			phone: [{
				rule: 'minLength', params: [10], prompt: t('payment.form.prompts.phone')
			}],
			payment_method: [{
				rule: 'number', prompt: t('payment.form.prompts.payment_method.empty'),
			}],
			zipcode: !data.order.paid ? [
				{ rule: v=>(v && v.length>=5 && !Number.isNaN(parseInt(v))), params: [5], prompt: t('payment.form.prompts.zipcode.minLength'), if: !data.order.needs_location },
			] : []
		});
		var method = formData.payment_method ? data.payment_methods.find(a=>a.payment_method==formData.payment_method) : null;
		
		if(!method && formData.payment_method) prompts.push(t('payment.form.prompts.payment_method.invalid'));
		setErrorPrompts(prompts);
		if(!valid) return;

		if(data.order.needs_location){
			var { valid: locValid, prompts: locPrompts } = Validator(location, {
				street: [{ rule: 'minLength', params: [3], label: t('payment.location.street') }],
				exterior_number: [{ rule: 'empty', label: t('payment.location.exterior_number') }],
				interior_number: [{ rule: 'empty', skipEmpty: true, label: t('payment.location.interior_number') }],
				neighborhood: [{ rule: 'minLength', params: [3], label: t('payment.location.neighborhood') }],
				city: [{ rule: 'minLength', params: [3], label: t('payment.location.city') }],
				zipcode: [
					{ rule: v=>(v && v.length>=5 && !Number.isNaN(parseInt(v))), params: [5], label: t('payment.location.zipcode') },
				],
			});
			setLocationPrompts(locPrompts)
			if(!locValid) return;
		}
		
		var card_token = null, device_fingerprint = null;
		if(method && method.type_id===PaymentMethodType.CARD){ // IS CARD
			if(!isWalletPayment){
				var card_number = cardForm.card_number.replace(/[^0-9]/gi, '')
				var { valid, prompts } = Validator(cardForm, {
					card_name: [{ rule: 'minLength', params: [4], prompt: t('payment.form.card.card_name') }],
					card_number: [{ rule: 'creditcard', prompt: t('payment.form.card.card_number') }],
					card_code: [{ rule: 'minLength', params: [3], prompt: t('payment.form.card.card_code') }],
					card_expiration: [
						{ rule: /[0-1][0-9]\/[0-9]{2}/gi, prompt: t('payment.form.card.card_expiration') },
						{ rule: v=>{
							var [month, year] = v.split('/');
							if(!month || !year) return false;
							var mm = parseInt(month), yy = parseInt(year);
							if(Number.isNaN(mm) || Number.isNaN(yy) || mm>12 || mm<0) return false;

							var current_mm = new Date().getMonth();
							var current_year = new Date().getFullYear();
							if((2000+yy)<current_year) return false;
							else if((2000+yy)==current_year){
								return mm>=(current_mm+1);
							}
							return true;
						}, prompt: t('payment.form.card.card_expiration') }
					],
					postal_code: [{ rule: 'minLength', params: [4], prompt: t('payment.form.card.postal_code') }],
				});
				setCardPrompts(prompts);
				if(!valid) return;
				try{
					var [month, year] = cardForm.card_expiration.split('/');
					setLoading(true);
					if(method.payment_method===PaymentMethods.PAYFRONT){
						var payfront_res = await Payfront.createToken({
							card_name: cardForm.card_name,
							card_number: card_number,
							card_cvv: cardForm.card_code,
							expiration: `${parseInt(month)}/${parseInt(year)}`,
							amount: total_final,
							postal_code: cardForm.postal_code,
						});
						if(payfront_res.error){
							setLoading(false);
							setCheckoutPrompts(payfront_res.message);
							return;
						}
						card_token = payfront_res.data.token;
					}else if(method.payment_method===PaymentMethods.OPENPAY_CARD){
						var openpay_res = await OpenPay.createToken(OPENPAY_MERCHANT, OPENPAY_KEY, {
							card_number: card_number,
							cvv2: cardForm.card_code,
							expiration_month: month,
							expiration_year: year,
							holder_name: cardForm.card_name,
							postal_code: cardForm.postal_code
						}, false);
						formData.payment_method = 2;
						card_token = openpay_res.token;
						device_fingerprint = openpay_res.device_fingerprint;
					}
				}catch(e){
					if(API.DEBUG) console.error(e);
					setLoading(false);
					setCheckoutPrompts(t('common.error_no_charge', { code: 'LCL-CKSBP-1' }));
					return;
				}
			}else{
				if(isWalletPayment===WalletType.APPLE_PAY){
					try{
						var apple_res = await Payfront.createApplePay({
							countryCode: 'MX',
							currencyCode: 'MXN',
							supportedNetworks: ['visa', 'masterCard', 'amex'],
							merchantCapabilities: ['supports3DS'],
							total: {
								label: t('common.site'),
								amount: total_final.toFixed(2)
							}
						});
						if(apple_res===null) return;
						if(apple_res.error){
							setCheckoutPrompts(apple_res.message);
							return;
						}
						card_token = apple_res.data.token;
					}catch(e){
						setCheckoutPrompts(t('common.error_no_charge', { code: 'LCL-CKSBP-2' }));
						return;
					}
				}
			}
			if(!card_token){
				setLoading(false);
				setCheckoutPrompts(t('common.error_no_charge', { code: 'LCL-CKSBP-3' }));
				return;
			}
		}

		setLoading(true);
		try{
			var res = await API.completeOrder(params.token, {
				first_name: formData.first_name,
				last_name: formData.last_name,
				email: formData.email,
				phone: formData.phone,
				zipcode: data.order.needs_location ? location.zipcode : formData.zipcode,
				payment_method: formData.payment_method,
				payment_plan: card_token ? ((cardForm?.payment_plan!=-1 ? cardForm?.payment_plan : null) || null) : null,
				card_token,
				device_fingerprint,
				location: data.order.needs_location ? location : null,
			});
			if(res.error) return setCheckoutPrompts(res.message);
			if(res.data.finished || res.data.paid){
				var dt = {...data};
				dt.order = {
					...dt.order,
					...formData
				}
				if(res.data.date_expiration){
					dt.order.payment_expiration = res.data.date_expiration;
				}
				dt.order.reference = res.data.reference;
				dt.order.payment_cost = res.data.payment_cost || payment_cost;
				dt.order.tickets_url = res.data.tickets_url;
				dt.order.payment_method = res.data.payment_method;
				dt.order.finished = true;
				dt.order.paid = !!res.data.paid;
				setData(dt);
			}else{
				if(res.data.redirect){
					window.location.href = res.data.redirect;
				}else{
					return setCheckoutPrompts(t('common.unexpected_code', { code: 'LCL-CKSBP-4' }));
				}
			}
		}catch(e){
			return setCheckoutPrompts(t('common.unexpected_code', { code: 'LCL-CKSBP-5' }));
		}finally{
			setLoading(false);
		}
	}

	var sendCoupon = (setLoading: SetLoading)=>{
		if(CodeData.data.applying) return;

		var { valid } = CodeData.validate();
		if(!valid) return;
		setLoading(true);
		setTimeout(()=>{
			API.getCouponToken(CodeData.data.code, uniqueArray(data.tickets.map(a=>a.date_id))).then(res=>{
				if(res.error) return CodeData.setPrompts([res.message]);
				if(res.data.found===false || !res.data.token){
					return CodeData.setPrompts([t('payment.coupon.invalid_code')]);
				}
				applyCoupon(res.data.token);
			}).catch(err=>{
				CodeData.setPrompts([t('common.unexpected_code', { code: 'LCL-CKTSDCP-1' })]);
			}).finally(()=>{
				setLoading(false);
			})
		}, API.DEBUG ? 500 : randomRange(1000, 2000));
	}

	var applyCoupon = (token: string)=>{
		CodeData.setValue('applying', true);
		API.applyCoupon(params.token, token).then(res=>{
			if(res.error) return CodeData.setPrompts([res.message]);
			if((!res.data.tickets || res.data.tickets.length==0) && !res.data.valid){
				return CodeData.setPrompts([t('payment.coupon.invalid_coupon')]);
			}
			var ctx = [...data.tickets];
			for(var i of res.data.tickets){
				var ix = ctx.findIndex(a=>a.ticket_hash===i.ticket_hash);
				if(ix==-1) continue;
				ctx[ix].ticket_cost = i.ticket_cost;
				ctx[ix].commission = i.commission;
				ctx[ix].original_cost = i.original_cost;
				ctx[ix].original_commission = i.original_commission;
			}
			setData({
				...data,
				tickets: ctx,
			});
			Toast.success(t('payment.coupon.applied'));
			setShownModal(null);
		}).catch(err=>{
			CodeData.setPrompts([t('common.unexpected_code', { code: 'LCL-CKTAPCP-1' })]);
		}).finally(()=>{
			CodeData.setValue('applying', false);
		});
	}

	var onPaymentMethodChange = (value: number)=>{
		if(value===-1){
			setIsWalletPayment(WalletType.APPLE_PAY);
			var card_payment = data.payment_methods.find(a=>a.payment_method===PaymentMethods.PAYFRONT);
			if(!card_payment) return;
			value = card_payment.payment_method;
		}else{
			setIsWalletPayment(null);
		}
		setFormData({ ...formData, payment_method: value });
	}

	var orderExpired = ()=>{
		if(!date_expiration) return;
		setExpired((date_expiration-2)<unix());
	}

	var has_form = data.order && !data.order.form_answered && data.tickets.find(a=>a.questions && a.questions.length>0);
	var onFormChange = bindFormChange(formData, setFormData);
	var onCardFormChange = bindFormChange(cardForm, setCardForm);
	var onLocationFormChange = bindFormChange(location, setLocation);

	var available_payment_plans = paymentPlans ? paymentPlans.filter(a=>(
		a.minimum_payment<(tickets_total+commission_total+data.order.delivery_cost)
	)) : [];

	var CommissionAmount = (props: { percent: number, amount: number })=>{
		var amount = props.amount+((tickets_total+commission_total+(data.order.paid ? 0 : data.order.delivery_cost))*props.percent);
		if(amount==0){
			return null;
		}

		return <div style={{ fontWeight: 'bold' }}>
			${addCommas(amount)}
		</div>
	}

	return <div className='ar checkout-screen'>
		{date_expiration ? (
			<div className="fr countdown message">
				{t('payment.countdown')}
				<Countdown className='header' unix={date_expiration} onFinish={orderExpired} />
			</div>
		) : null}
		{has_form ? (
			<CheckoutForm token={params.token} order={data} onFormFinish={formFinish} />
		) : (
			<div className='ar checkout'>
				<div className='column'>
					<Groupper title={t('payment.buyer')}>
						<Field amount={2}>
							<Input label={t('payment.fields.buyer_first_name')} value={formData.first_name} onChange={onFormChange('first_name')} readonly={sending || data.order.paid} />
							<Input label={t('payment.fields.buyer_last_name')} value={formData.last_name} onChange={onFormChange('last_name')} readonly={sending || data.order.paid} />
						</Field>
						<Input label={t('payment.fields.buyer_email')} value={formData.email} onChange={onFormChange('email')} readonly={sending || data.order.paid} />
						<Input label={t('payment.fields.phone')} value={formData.phone} onChange={onFormChange('phone')} readonly={sending || data.order.paid} />
						{(!data.order.needs_location && !data.order.paid) && (
							<Input label={t('payment.fields.zipcode')} value={formData.zipcode} onChange={onFormChange('zipcode')} readonly={sending || data.order.paid} />
						)}
						{data.order.paid ? (
							<Message type="info" text={t('payment.form.cant_change')} />
						) : null}
						<Message list={errorPrompts} type='error' style={{ marginBottom: 0, marginTop: 10 }} />
					</Groupper>
					{!!paymentError && (
						<Message type='error' style={{ marginTop: 10 }}>
							{paymentError}
						</Message>
					)}
					<Groupper title={t('payment.payment_method')} style={{ marginTop: 10 }} fitted>
						<Accordion divided selectable canUnselect={false} toggle managed={false} selected={formData?.payment_method} onSelected={onPaymentMethodChange}>
							{!!hasApplePayMethod && (
								<Accordion.Item key={'applewallet'} active={isWalletPayment===WalletType.APPLE_PAY} grayed={formData?.payment_method && isWalletPayment!==WalletType.APPLE_PAY} className='wallet' value={-1} onSelected={bindClose(setIsWalletPayment, WalletType.APPLE_PAY)} title={(
									<Image src='/applepay.svg' />
								)} rightTitle={<CommissionAmount percent={data.order.card_commission || 0} amount={0} />} />
							)}
							{data.payment_methods.map(a=>(
								a.type_id===PaymentMethodType.CARD ? ( // IS CARD
									<Accordion.Item key={`pm-ck-${a.payment_method}`} value={a.payment_method} title={a.method_name} active={formData?.payment_method===a.payment_method && !isWalletPayment} grayed={!!formData?.payment_method && (!!isWalletPayment || formData?.payment_method!==a.payment_method)} rightTitle={(
										<CommissionAmount percent={data.order.card_commission || a.commission_percent} amount={a.commission_amount} />
									)}>
										<div>
											<Input label={t('payment.fields.card_name')} value={cardForm.card_name} onChange={onCardFormChange('card_name')} placeholder={t('payment.fields.card_name_placeholder')} readonly={sending} />
											<Input label={t('payment.fields.card_number')} value={cardForm.card_number} onChange={onCardFormChange('card_number')} placeholder={t('payment.fields.card_number_placeholder')} maxLength={16+3} valueFormat={formatCreditCard} inputStyle={{ textAlign: 'center', fontWeight: 'bold', fontSize: 16 }} readonly={sending} />
											<Field amount={2}>
												<Input label={t('payment.fields.card_expiration')} value={cardForm.card_expiration} onChange={onCardFormChange('card_expiration')} placeholder='MM/YY' valueFormat={formatExpiration} maxLength={5} readonly={sending} />
												<Input label={(t('payment.fields.card_code'))} value={cardForm.card_code} onChange={onCardFormChange('card_code')} placeholder={t('payment.fields.card_code_placeholder')} maxLength={5} readonly={sending} />
											</Field>
											<Input label={t('payment.fields.zipcode')} value={cardForm.postal_code} onChange={onCardFormChange('postal_code')} readonly={sending} />
											{!!paymentPlans && available_payment_plans?.length>0 && (
												<Dropdown label={t('payment.fields.payment_plan')} onChange={onCardFormChange('payment_plan')}>
													<Dropdown.Item text={'Contado'} value={-1} />
													{available_payment_plans.map(a=>(
														<Dropdown.Item key={`PPLN-${a.plan_id}`} text={`${a.months} meses ${a.interest ? 'sin intereses' : 'con intereses'}`} value={a.plan_id} />
													))}
												</Dropdown>
											)}
											<Message list={cardPrompts} type='error' style={{ marginBottom: 0, marginTop: 10 }} />
										</div>
									</Accordion.Item>
								) : (
									<Accordion.Item 
										key={`pm-ck-${a.payment_method}`} 
										value={a.payment_method} 
										title={a.method_name} 
										active={formData?.payment_method===a.payment_method} 
										grayed={!!formData?.payment_method && (!!isWalletPayment || formData?.payment_method!==a.payment_method)} 
										rightTitle={(
											<CommissionAmount percent={a.commission_percent} amount={a.commission_amount} />
										)} 
									/>
								)
							))}
						</Accordion>
					</Groupper>
					{!!data.order.needs_location && (
						<Groupper title={t('payment.location.title')} style={{ marginTop: 10 }}>
							<Input label={t('payment.location.street')} value={location.street} onChange={onLocationFormChange('street')} />
							<Field amount={2}>
								<Input label={t('payment.location.exterior_number')} value={location.exterior_number} onChange={onLocationFormChange('exterior_number')} />
								<Input label={t('payment.location.interior_number')} value={location.interior_number} onChange={onLocationFormChange('interior_number')} />
							</Field>
							<Field amount={2}>
								<Input label={t('payment.location.neighborhood')} value={location.neighborhood} onChange={onLocationFormChange('neighborhood')} />
								<Input label={t('payment.location.city')} value={location.city} onChange={onLocationFormChange('city')} />
							</Field>
							<Field amount={2}>
								<Input label={t('payment.location.state')} value={location.state} onChange={onLocationFormChange('state')} />
								<Input label={t('payment.location.zipcode')} value={location.zipcode} onChange={onLocationFormChange('zipcode')} />
							</Field>
							<Message list={locationPrompts} type='error' />
						</Groupper>
					)}
					{OrderIdentifier}
				</div>
				<div className='column small'>
					<Groupper title={t('payment.totals.title')} fitted actions={(
						isWalletPayment===WalletType.APPLE_PAY ? (
							<ApplePayButton style={{ padding: 15, borderRadius: 100, height: 38, cursor: 'pointer' }} onClick={submitPayment} type='buy' />
						) : (
							<Button fluid color='black' text={t('payment.totals.button')} size='big' onClick={submitPayment} onLoadingChanged={setSending} />
						)
					)}>
						<table className="fr striped table">
							<tbody>
								<tr>
									<td>{t('payment.totals.tickets')}</td>
									<td className="collapsing">
										{!!has_discount && (
											<span className="discount">${addCommas(original_tickets_total)}</span>
										)}
										${addCommas(tickets_total)}
									</td>
								</tr>
								<tr>
									<td>{t('payment.totals.commission')}</td>
									<td className="collapsing">
										{!!has_discount_commission && (
											<span className="discount">${addCommas(original_commission_total)}</span>
										)}
										${addCommas(commission_total)}
									</td>
								</tr>
								<tr>
									<td>{t('payment.totals.payment_method')}</td>
									<td className="collapsing">
										{formData.payment_method ? (
											`$${addCommas(payment_cost)}`
										) : (
											<Icon color='grey' name='minus' />
										)}
									</td>
								</tr>
								<tr>
									<td>{t('payment.totals.delivery')}</td>
									<td className="collapsing">${addCommas(data.order.delivery_cost)}</td>
								</tr>
								{!!(has_discount || has_discount_commission) && (
									<tr>
										<td>{t('payment.totals.discount')}</td>
										<td>
											-${addCommas((original_tickets_total-tickets_total)+(original_commission_total-commission_total))}
										</td>
									</tr>
								)}
								<tr className="totals">
									<td>{t('payment.totals.total')}</td>
									<td className="collapsing">
										{(!!has_discount || !!has_discount_commission) && (
											<span className="discount">
												${addCommas(original_tickets_total+original_commission_total+payment_cost+data.order.delivery_cost)}
											</span>
										)}
										${addCommas(tickets_total+commission_total+payment_cost+data.order.delivery_cost)}
									</td>
								</tr>
								{checkoutPrompts && checkoutPrompts.length>0 ? (
									<tr className='totals'>
										<td colSpan={2}>
											<Message type='error' list={[checkoutPrompts]} style={{ marginBottom: 0, textAlign: 'left' }} />
										</td>
									</tr>
								) : null}
							</tbody>
						</table>
					</Groupper>
					<Button style={{ marginTop: 10, borderRadius: 8 }} fluid text={t('payment.add_coupon')} iconName='tag' onClick={bindClick(setShownModal, Modals.COUPON)} />
					<Groupper className='tickets' title={t('payment.tickets.title')} fitted>
						<div className="fr items small tickets divided">
							{data.tickets.map(a=>(
								<div className="item" key={`tkt-${a.ticket_hash}`}>
									<Image src={`${CDN_URL}/events/${a.event_id}/800.webp`} alt={a.event_name} fallback={PLACEHOLDER_IMG} />
									<div className="info">
										<div className="event">{a.event_name}</div>
										<div className="date">{moment.unix(a.ticket_date || a.date).format('DD/MMM/YY HH:mm')}</div>
										<div className="section meta">
											{a.section_name} - {a.price_name}
										</div>
										{a.numbered ? (
											<div className="seat meta">
												{a.seat_section ? (`${a.seat_section} - `) : t('common.seat')} {formatSeatNumber(a.seat_row, a.seat_number)}
											</div>
										) : null}
										{a.paid ? (
											<div className="paid">{t('payment.tickets.paid')}</div>
										) : (
											<div className="cost">
												{(toCents(a.ticket_cost+a.commission)!=toCents(a.original_cost+a.original_commission)) && (
													<div className="discount">${addCommas(a.original_cost+a.original_commission)}</div>
												)}
												${addCommas(a.ticket_cost+a.commission)}
											</div>
										)}
									</div>
								</div>
							))}
						</div>
					</Groupper>
				</div>
			</div>
		)}
		<Modal open={shownModal===Modals.COUPON} onClose={!CodeData.data.applying ? bindClick(setShownModal) : null} size='tiny'>
			<Modal.Header>{t('payment.add_coupon')}</Modal.Header>
			<Modal.Content>
				{!CodeData.data.applying ? <>
					<Header text={t('payment.coupon.header')} subtext={t('payment.coupon.subheader')} iconName='tag' />
					<Input label='Código' value={CodeData.data.code} error={CodeData.errors.code} onChange={CodeData.onDataChange('code')} labelStyle={{ textAlign: 'center' }} inputStyle={{ textAlign: 'center', fontSize: 16, textTransform: 'uppercase' }} />
					<Message list={CodeData.prompts} type='error' />
				</> : <>
					<Header text={t('payment.coupon.applying_coupon')} size='big' style={{ fontSize: 24 }} containerStyle={{ marginBottom: 5 }} subtext={t('payment.coupon.applying_coupon_text')} loading />
					{!!CodeData.data.code && CodeData.data.code.length>=3 && (
						<div style={{ textAlign: 'center', color: 'gray', fontWeight: 'bold' }}>{t('payment.coupon.code', { code: CodeData.data.code?.toUpperCase() })}</div>
					)}
					<div style={{ height: 40 }} />
				</>}
			</Modal.Content>
			{!CodeData.data.applying && (
				<Modal.Actions>
					<Button text={t('common.close')} basic onClick={bindClick(setShownModal)} />
					<Button text={t('common.add')} color='black' onClick={sendCoupon} />
				</Modal.Actions>
			)}
		</Modal>
	</div>
}

// var PaymentContainer = (props: PaymentScreenProps)=>{
// 	return <ApplePayProvider merchantId='merchant.mx.payfront.applepay'>
// 		<Payment {...props} />
// 	</ApplePayProvider>
// }

export default Payment;